'use client';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import Label from '../Label';

type SelectItem = {
  value: string;
  text: string;
  disabled?: boolean;
};

type Props = {
  items: SelectItem[];
  ariaLabel: string;
  label?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  defaultValue?: string;
  emptyText?: string;
  onValueChange?: (value: string) => void;
  fluid?: boolean;
  required?: boolean;
  disabled?: boolean;
  showEmpty?: boolean;
  variant?: 'default' | 'search';
  error?: string;
};

function Select(props: Props) {
  const {
    items,
    id,
    name,
    required,
    defaultValue,
    ariaLabel,
    onValueChange,
    fluid,
    label,
    disabled,
    showEmpty,
    variant = 'default',
    emptyText = 'Keine Angabe',
    error,
  } = props;

  return (
    <div className={clsx('select', { 'select--fluid': fluid, 'select--search': variant === 'search' })}>
      {label ? <Label htmlFor={name ?? id} text={label} /> : null}
      <SelectPrimitive.Root
        defaultValue={defaultValue ? defaultValue : showEmpty ? 'empty' : items?.[0].value}
        onValueChange={onValueChange}
        name={name ?? id}
        required={required}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger className='select__trigger' aria-label={ariaLabel} id={name ?? id}>
          <SelectPrimitive.Value className='select__value' />
          <SelectPrimitive.Icon className='select__icon'>
            <ChevronDownIcon width={32} height={32} />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Content className='select__content' position='popper'>
          <SelectPrimitive.ScrollUpButton>
            <ChevronUpIcon />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className='select__viewport'>
            {showEmpty ? (
              <SelectPrimitive.Item className='select__item' value='empty'>
                <SelectPrimitive.ItemIndicator className='select__item-indicator'>
                  <CheckIcon width={32} height={32} />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText className='select__item-text'>{emptyText}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ) : null}
            {items.map(item => (
              <SelectPrimitive.Item
                key={item.value}
                className='select__item'
                value={item.value}
                disabled={item.disabled}
              >
                <SelectPrimitive.ItemIndicator className='select__item-indicator'>
                  <CheckIcon width={32} height={32} />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText className='select__item-text'>{item.text}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton>
            <ChevronDownIcon />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Root>
      {error ? <p>{error}</p> : null}
    </div>
  );
}

export default Select;
